import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
  Plane, 
  Building2, 
  PartyPopper, 
  ChevronRight,
  CheckCircle2,
  Clock,
  Shield,
  Star,
  FileText,
  Wifi,
  Battery,
  Snowflake,
  Users
} from 'lucide-react';

const IMAGES = {
  airport: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1200&q=80",
  corporate: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?auto=format&fit=crop&w=1200&q=80",
  events: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1200&q=80",
};

export const ServicesPage = () => {
  const { t } = useLanguage();

  const services = [
    {
      id: 'aeropuerto',
      icon: Plane,
      title: t('services.airport.title'),
      subtitle: t('services.airport.subtitle'),
      description: t('services.airport.description'),
      image: IMAGES.airport,
      features: t('services.airport.features'),
      benefits: [
        { icon: Clock, text: t('hero.features.punctuality') },
        { icon: Shield, text: 'GPS' },
        { icon: Wifi, text: 'WiFi' },
        { icon: FileText, text: t('hero.features.billing') },
      ],
    },
    {
      id: 'corporativo',
      icon: Building2,
      title: t('services.corporate.title'),
      subtitle: t('services.corporate.subtitle'),
      description: t('services.corporate.description'),
      image: IMAGES.corporate,
      features: t('services.corporate.features'),
      benefits: [
        { icon: Users, text: t('services.corporate.subtitle') },
        { icon: FileText, text: t('hero.features.billing') },
        { icon: Star, text: t('hero.features.vehicles') },
        { icon: Battery, text: 'USB' },
      ],
    },
    {
      id: 'eventos',
      icon: PartyPopper,
      title: t('services.events.title'),
      subtitle: t('services.events.subtitle'),
      description: t('services.events.description'),
      image: IMAGES.events,
      features: t('services.events.features'),
      benefits: [
        { icon: Star, text: 'Premium' },
        { icon: Snowflake, text: t('vehicle.climate') },
        { icon: Shield, text: t('vehicle.insurance') },
        { icon: FileText, text: t('hero.features.billing') },
      ],
    },
  ];

  const vehicleFeatures = [
    { icon: Wifi, title: t('vehicle.wifi'), description: t('vehicle.wifiDesc') },
    { icon: Battery, title: t('vehicle.chargers'), description: t('vehicle.chargersDesc') },
    { icon: Snowflake, title: t('vehicle.climate'), description: t('vehicle.climateDesc') },
    { icon: Shield, title: t('vehicle.insurance'), description: t('vehicle.insuranceDesc') },
  ];

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 noise-overlay" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="text-sm tracking-widest uppercase text-sky-400 font-medium mb-4 block">
              {t('services.sectionLabel')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {t('services.title').split(' ').slice(0, 3).join(' ')}{' '}
              <span className="text-gradient">{t('services.title').split(' ').slice(-1)}</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              {t('services.subtitle')}
            </p>
            <div className="badge-billing bg-emerald-900/30 border-emerald-700 text-emerald-400">
              <FileText className="w-4 h-4" />
              <span>{t('hero.badge')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          data-testid={`service-section-${service.id}`}
          className={`section-padding ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
        >
          <div className="container-custom">
            <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center">
                    <service.icon className="w-7 h-7 text-sky-600" />
                  </div>
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                      {service.title}
                    </h2>
                    <p className="text-sky-600 font-medium">{service.subtitle}</p>
                  </div>
                </div>

                <p className="text-slate-500 leading-relaxed text-lg mb-8">
                  {service.description}
                </p>

                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-4 mb-8">
                  {service.benefits.map((benefit, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2"
                    >
                      <benefit.icon className="w-4 h-4 text-sky-600" />
                      <span className="text-sm font-medium text-slate-700">{benefit.text}</span>
                    </div>
                  ))}
                </div>

                <Link to="/contacto#reservar">
                  <Button 
                    data-testid={`btn-reserve-${service.id}`}
                    className="btn-primary"
                  >
                    {t('services.bookThis')}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>

              {/* Image */}
              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-emerald-500 text-white rounded-2xl p-4 shadow-xl">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    <span className="font-medium">{t('services.withInvoice')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Vehicle Features */}
      <section className="section-padding bg-slate-900">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-sm tracking-widest uppercase text-sky-400 font-medium mb-4 block">
              {t('vehicle.sectionLabel')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('vehicle.title')}
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              {t('vehicle.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {vehicleFeatures.map((feature, index) => (
              <div
                key={index}
                data-testid={`vehicle-feature-${index}`}
                className="bg-slate-800/50 rounded-2xl p-6 text-center hover:bg-slate-800 transition-colors"
              >
                <div className="w-14 h-14 bg-sky-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-sky-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Note */}
      <section className="py-16 bg-sky-500">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 text-white">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-2">
                {t('services.quoteTitle')}
              </h3>
              <p className="text-sky-100 text-lg">
                {t('services.quoteSubtitle')}
              </p>
            </div>
            <Link to="/contacto#reservar">
              <Button 
                data-testid="btn-quote"
                className="bg-white text-sky-600 hover:bg-sky-50 rounded-full px-8 py-4 font-medium transition-all duration-300 hover:scale-105"
              >
                {t('services.requestQuote')}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <h2 className="section-title mb-6">
            {t('services.bookToday')}
          </h2>
          <p className="section-subtitle max-w-2xl mx-auto mb-8">
            {t('services.bookTodaySubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto#reservar">
              <Button 
                data-testid="btn-cta-services"
                className="btn-accent text-lg px-12 py-5"
              >
                {t('services.makeReservation')}
                <ChevronRight className="w-6 h-6 ml-2" />
              </Button>
            </Link>
            <Link to="/contacto">
              <Button 
                data-testid="btn-contact-services"
                variant="outline"
                className="btn-secondary text-lg px-12 py-5"
              >
                {t('services.contactUs')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
